import { useEffect, useState } from "react";
import { preloadCompressedVehicleImage } from "@/utils/compressed-vehicle-image";
import { getConvertedHeicBlob, isHeicUrl } from "@/utils/image-display";

type ImageSource = { key?: string; url: string | null | undefined };
type ImageState = {
  source: string;
  imageUrl: string | null;
  isLoading: boolean;
  error: Error | null;
};

/** Resolves compressed variants and converts HEIC, owning each display URL's lifetime. */
export function useDisplayImage(image: ImageSource | null | undefined) {
  const url = image?.url?.trim() ?? "";
  const key = image?.key;
  const source = JSON.stringify([key, url]);
  const [state, setState] = useState<ImageState | null>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ source, imageUrl: null, isLoading: true, error: null });

    const resolve = async () => {
      try {
        let resolvedUrl = await preloadCompressedVehicleImage({ key, url });
        if (cancelled) return;
        if (isHeicUrl(resolvedUrl)) {
          const blob = await getConvertedHeicBlob(resolvedUrl);
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          resolvedUrl = objectUrl;
        }
        setState({ source, imageUrl: resolvedUrl, isLoading: false, error: null });
      } catch (error) {
        if (!cancelled) {
          setState({
            source,
            imageUrl: null,
            isLoading: false,
            error: error instanceof Error ? error : new Error("Failed to display image"),
          });
        }
      }
    };
    void resolve();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [key, url, source]);

  return url && state?.source === source
    ? state
    : { imageUrl: null, isLoading: Boolean(url), error: null };
}
